import React, { useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';

import { Col, Row } from 'react-bootstrap';

import useBeforeUnload from 'hooks/useBeforeUnload';
import AppContext from 'config/app-context';
import useExamFocusGuard from 'hooks/useExamFocusGuard';
import DisableCopyPasteDevTools from 'hooks/disableCopyPasteDevTools';

import Section from 'components/common/Section';
import Header from './header/Header';
import Layout from './wizard/layout/Layout';

import useFullscreenMode from 'hooks/certification/useFullscreenMode';
import useSessionReloadHandler from 'hooks/certification/useSessionReloadHandler';
import useCertificationLifecycle from 'hooks/certification/useCertificationLifecycle';
import useCertificationErrorReport from 'hooks/certification/useCertificationErrorReport';

/**
 * Componente principal para renderizar el examen de certificación EPST.
 *
 * Este componente se encarga de:
 * - Cargar y validar la certificación desde la ubicación actual.
 * - Configurar el modo de pantalla completa.
 * - Manejar reinicios de sesión para marcar intentos cancelados.
 * - Controlar el enfoque del navegador para detectar infracciones del examen.
 * - Cargar el ciclo de vida de la certificación (por ejemplo, evitar exámenes repetidos).
 * - Manejar errores e irregularidades durante el examen.
 * - Deshabilitar acciones prohibidas como copiar/pegar o abrir herramientas de desarrollador.
 *
 * Hooks personalizados utilizados:
 * - `useBeforeUnload`: Previene la pérdida de progreso si el usuario abandona la página.
 * - `useFullscreenMode`: Fuerza el modo pantalla completa durante el examen.
 * - `useSessionReloadHandler`: Detecta si la página fue recargada y cancela el examen.
 * - `useCertificationLifecycle`: Verifica si la certificación es válida antes de comenzar.
 * - `useCertificationErrorReport`: Genera reportes si el examen se interrumpe por errores.
 * - `useExamFocusGuard`: Detecta si el usuario cambia de pestaña o pierde el enfoque.
 *
 * Subcomponentes:
 * - `Header`: Encabezado con logo o navegación (opcional).
 * - `Layout`: Estructura principal del examen, incluyendo preguntas y navegación entre pasos.
 *
 * @returns {JSX.Element} Componente renderizado para el flujo de certificación EPST.
 */
const CertificationEpst = () => {
	const token = useSelector(state => state.authentication.user?.token);
	const username = useSelector(state => state.authentication.user?.username);
	const { step } = useSelector(state => state.certificationReducer);

	const navigate = useNavigate();
	const location = useLocation();
	const dispatch = useDispatch();

	const { config, setConfig } = useContext(AppContext);
	const certification = location.state;

	useBeforeUnload();
	useFullscreenMode({ config, setConfig });
	useSessionReloadHandler({
		certification,
		token,
		onReload: () => handleUpdateCertificationStatusById({ status: 5 })
	});
	useCertificationLifecycle({ certification, token, dispatch, navigate });

	const { handleUpdateCertificationStatusById } = useCertificationErrorReport({
		certification,
		token,
		dispatch,
		navigate,
		username
	});

	/*const { violationCount, resetGuard } = useExamFocusGuard(
		(count) => {
			if (count === 4) {
				handleUpdateCertificationStatusById({ status: 4 });
				resetGuard();
			}
		},
		() => {
			handleUpdateCertificationStatusById({ status: 4 });
			resetGuard();
		},
		step,
		certification.certification_type
	);*/

	return (
		<>
			<DisableCopyPasteDevTools />
			<Section fluid className="py-0 fullscreen">
				<div className="container">
					<Row className="justify-content-center">
						<Col lg={11} xxl={11} className="py-3">
							<Header />
							<Layout
								variant="pills"
								validation={true}
								progressBar={true}
								certification={certification}
							/>
						</Col>
					</Row>
				</div>
			</Section>
		</>
	);
};

export default CertificationEpst;